import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Skeleton, Empty } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import httpClient from '../../utils/HttpClient';
import { MAGAZINE } from '../../constants/Url';
import NotFoundPage from '../NotFoundPage';
import AuthModal from '../../components/auth/AuthModal';
import MagazineModal from '../../components/MagazineModals';
import PROModal from '../../components/PROModal';
import { useMemberAccess } from '../../hooks/useMemberAccess';

export default function MagazineDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const authModalRef = useRef();
  const appDownloadRef = useRef();
  const user = useSelector((state) => state.authReducer);
  let token = user && user?.token ? user?.token : null;
  const { hasNoActiveMembership } = useMemberAccess();
  const [isMemberGateOpen, setIsMemberGateOpen] = useState(false);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const fetchDetail = async () => {
    try {
      setLoading(true);
      const res = await httpClient.get(`${MAGAZINE}/${id}`)
        .then(res => res.data)
        .catch(error => ({ status: 500, message: error?.message }));

      if (res && res.status === 200 && res.data) {
        setData(res.data);
        setNotFound(false);
      } else {
        setNotFound(true);
      }
    } catch (error) {
      console.error('fetchDetail -> error', error);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchDetail();
    }
  }, [id]);

  useEffect(() => {
    if (user?.token && hasNoActiveMembership()) {
      setIsMemberGateOpen(true);
    }
  }, [user?.token, hasNoActiveMembership]);

  const handleShowDownloadApp = (item) => {
    if (!item) return;
    if (!token) {
      authModalRef.current.open();
      return;
    }
    if (!user?.user?.completedTS) {
      authModalRef.current.open('REGISTER');
      return;
    }
    // Gate: expired trial or no membership cannot read magazine
    if (hasNoActiveMembership()) {
      setIsMemberGateOpen(true);
      return;
    }
    appDownloadRef.current?.showModal(item);
  };

  if (notFound) {
    return <NotFoundPage />;
  }

  return (
    <div className="w-full min-h-screen pb-16 bg-[#f7f9fc]">
      <AuthModal ref={authModalRef} />
      
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-2">
        <div 
          onClick={() => {
            if (window.history.state && window.history.state.idx > 0) {
              navigate(-1);
            } else {
              navigate('/magazine');
            }
          }} 
          className="inline-flex items-center gap-2 cursor-pointer text-gray-500 hover:text-black transition-colors font-medium text-sm sm:text-base"
        >
          <ArrowLeftOutlined className="text-base" />
          <span>{t('text_back_previous_page')}</span>
        </div>
      </div>

      {loading || !data ? (
        /* Skeleton */
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 mt-4 flex flex-col gap-10">
          <section className="w-full">
            <div className="flex justify-between items-center mb-4 sm:mb-6 pb-2 border-b border-gray-100">
              <h2 className="text-[17px] sm:text-[20px] font-bold text-gray-900 tracking-wide m-0">
                {t('text_read_magazine')}
              </h2>
              <span className="text-[12px] sm:text-[13px] text-gray-400 font-medium whitespace-nowrap">
                {t('button_view_more')}
              </span>
            </div>
            <div className="flex flex-col md:flex-row overflow-hidden bg-white rounded-lg shadow-sm border border-gray-50 p-4 gap-4">
              <div className="w-full md:w-[45%] h-[250px] sm:h-[380px] md:h-[480px]">
                <div className="w-full h-full bg-gray-100 animate-pulse rounded flex items-center justify-center">
                  <svg className="w-12 h-12 text-gray-300" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" fill="currentColor" viewBox="0 0 640 512">
                    <path d="M480 80C480 35.82 515.8 0 560 0C604.2 0 640 35.82 640 80C640 124.2 604.2 160 560 160C515.8 160 480 124.2 480 80zM0 456.1C0 445.6 2.964 435.3 8.551 426.4L225.3 81.01C231.9 70.42 243.5 64 256 64C268.5 64 280.1 70.42 286.8 81.01L412.7 281.7L460.9 202.7C464.1 196.1 472.2 192 480 192C487.8 192 495 196.1 499.1 202.7L631.1 419.1C636.9 428.6 640 439.7 640 450.9C640 484.6 612.6 512 578.9 512H55.91C25.03 512 .0006 486.1 .0006 456.1L0 456.1z"/>
                  </svg>
                </div>
              </div>
              <div className="w-full md:w-[55%] p-6 sm:p-10 md:p-16 flex flex-col justify-center">
                <Skeleton.Input active block size="large" />
                <Skeleton active paragraph={{ rows: 4 }} className="mt-4" />
                <Skeleton.Button active size="large" className="mt-8 w-[260px]" style={{ width: '260px', height: '48px' }} />
              </div>
            </div>
          </section>
        </div>
      ) : (
        /* Single Detail Mode (Matching Fashion UI) */
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 mt-4 flex flex-col gap-10">
          <section className="w-full">
            <div className="flex justify-between items-center mb-4 sm:mb-6 pb-2 border-b border-gray-100">
              <h2 className="text-[17px] sm:text-[20px] font-bold text-gray-900 tracking-wide m-0">
                {t('text_read_magazine')}
              </h2>
              <span
                onClick={() => navigate('/magazine')}
                className="text-[12px] sm:text-[13px] text-gray-400 hover:text-black cursor-pointer font-medium transition-colors whitespace-nowrap"
              >
                {t('button_view_more')}
              </span>
            </div>

            <div className="flex flex-col md:flex-row overflow-hidden bg-white rounded-lg shadow-sm border border-gray-50">
              {/* Image */}
              <div className="w-full md:w-[45%] h-[250px] sm:h-[380px] md:h-[480px] overflow-hidden relative flex-shrink-0">
                <img
                  src={data.picUrl || data.coverimage || data.imagePath}
                  alt={data.title}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700 cursor-pointer"
                  onClick={() => handleShowDownloadApp(data)}
                />
              </div>

              {/* Details */}
              <div className="w-full md:w-[55%] p-6 sm:p-10 md:p-16 flex flex-col justify-center items-start gap-4">
                {data.releaseYear && (
                  <span className="text-[14px] sm:text-[16px] font-semibold tracking-wider uppercase bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 bg-clip-text text-transparent">
                    {data.releaseYear}
                  </span>
                )}
                
                <h3 className="text-[28px] md:text-[34px] font-bold text-gray-950 leading-tight mb-2">
                  {data.title}
                </h3>

                <p className="text-gray-500 text-[14px] sm:text-[15px] leading-relaxed line-clamp-4 m-0 whitespace-pre-wrap">
                  {data.description || data.subtitle || data.intro || ''}
                </p>

                {(data.magazineId || data._id || data.id) && (
                  <div className="w-full sm:w-auto mt-8 p-[2px] bg-gradient-to-r from-[#50c7f8] via-[#7887fd] to-[#df97fb] rounded-md shadow-md">
                    <button
                      onClick={() => handleShowDownloadApp(data)}
                      className="w-full sm:w-[260px] h-12 flex items-center justify-center bg-gradient-to-r from-[#21163e] to-[#252554] text-white hover:opacity-90 transition-all rounded-md"
                    >
                      <span className="text-[13px] sm:text-[14px] tracking-widest text-center font-medium">
                        {t('text_read_now')}
                      </span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </section>
        </div>
      )}

      <MagazineModal ref={appDownloadRef} />
      <PROModal
        open={isMemberGateOpen}
        onCancel={() => setIsMemberGateOpen(false)}
      />
    </div>
  );
}
